import { createContext, useContext, useState } from "react";
import { FilteringOperator } from "../models/person.model";

// Define the shape of our search configuration
export interface SearchConfig {
  matchTechThreshold: number;
  matchCertThreshold: number;
  maxTechMatches: number;
  maxCertMatches: number;
  exactMatchOnly: boolean;
  exactTechMatchOnly: boolean;
  exactCertMatchOnly: boolean;
  useTechSearch: boolean;
  useCertSearch: boolean;
  filteringOperator: FilteringOperator;
  debounceDelay: number; // Debounce delay in milliseconds
}

// Default configuration values
const defaultConfig: SearchConfig = {
  matchTechThreshold: 0.85,
  matchCertThreshold: 0.85,
  maxTechMatches: 5,
  maxCertMatches: 5,
  exactMatchOnly: false,
  exactTechMatchOnly: false,
  exactCertMatchOnly: false,
  useTechSearch: true,
  useCertSearch: true,
  filteringOperator: FilteringOperator.Or,
  debounceDelay: 1200, // 1.2 seconds default
};

// Define the context shape
interface SearchConfigContextType {
  config: SearchConfig;
  updateConfig: (newConfig: Partial<SearchConfig>) => void;
  resetConfig: () => void;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

// Create the context
const SearchConfigContext = createContext<SearchConfigContextType | undefined>(
  undefined
);

// Local storage key
const STORAGE_KEY = "cv_portal_search_config";

// Provider component
export function SearchConfigProvider({ children }: { children: React.ReactNode }) {
  // Read from localStorage synchronously as initial value to avoid a double-fetch.
  // Avoid setting state in a mount effect because that retriggers list-data requests.
  const [config, setConfig] = useState<SearchConfig>(() => {
    try {
      const storedConfig = localStorage.getItem(STORAGE_KEY);
      if (storedConfig) {
        const parsedConfig = JSON.parse(storedConfig);
        const migratedConfig = {
          ...parsedConfig,
          useTechSearch:
            typeof parsedConfig.useTechSearch === "boolean"
              ? parsedConfig.useTechSearch
              : !parsedConfig.skipDictionaryMatching,
          useCertSearch:
            typeof parsedConfig.useCertSearch === "boolean"
              ? parsedConfig.useCertSearch
              : !parsedConfig.skipDictionaryMatching,
        };

        return { ...defaultConfig, ...migratedConfig };
      }
    } catch (error) {
      console.error("Failed to parse stored search configuration:", error);
      localStorage.removeItem(STORAGE_KEY);
    }
    return defaultConfig;
  });

  // State for the modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Update config and save to local storage
  const updateConfig = (newConfig: Partial<SearchConfig>) => {
    const updatedConfig = { ...config, ...newConfig };
    setConfig(updatedConfig);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedConfig));
  };

  // Reset to default config
  const resetConfig = () => {
    setConfig(defaultConfig);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaultConfig));
  };

  // Modal controls
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  return (
    <SearchConfigContext.Provider
      value={{
        config,
        updateConfig,
        resetConfig,
        isModalOpen,
        openModal,
        closeModal,
      }}
    >
      {children}
    </SearchConfigContext.Provider>
  );
}

// Custom hook for using the search config
export function useSearchConfig() {
  const context = useContext(SearchConfigContext);
  if (context === undefined) {
    throw new Error("useSearchConfig must be used within a SearchConfigProvider");
  }
  return context;
}
